// 2026-09-21 - FormuLearning - 우클릭 메뉴 공용 부품 (이슈 #77 G6, COMP-CONTEXT-MENU)
// 파일 위치: frontend/src/components/common/ContextMenu.tsx
//
// 커서 위치(clientX·Y)에 뜨는 메뉴. `Popover` 와 같은 이유로 **body 로 포털**해
// `position: fixed` 로 띄운다(좌측 패널은 overflow 컨테이너라 absolute 면 잘린다).
// 화면 밖으로 넘치면 안쪽으로 당긴다. 바깥 클릭·Esc·스크롤·창 크기 변경에 닫힌다.
// 하위 메뉴는 두지 않는다 — «Move to…» 처럼 목록이 필요한 항목은 호출부가 메뉴를
// 같은 자리에 다른 항목으로 다시 연다.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface MenuItem {
  label: string
  onSelect?: () => void
  disabled?: boolean
  danger?: boolean
  /** 들여쓰기 단계(폴더 경로 목록용) */
  depth?: number
  /** 구분선만 그린다 */
  divider?: boolean
}

interface Props {
  x: number
  y: number
  items: MenuItem[]
  onClose: () => void
  /** 접근성 라벨 */
  label?: string
}

const MARGIN = 8

export default function ContextMenu({ x, y, items, onClose, label }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: y, left: x })

  // 그려진 크기를 안 뒤 화면 안으로 당긴다.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { offsetWidth: w, offsetHeight: h } = el
    setPos({
      left: Math.max(MARGIN, Math.min(x, window.innerWidth - w - MARGIN)),
      top: Math.max(MARGIN, Math.min(y, window.innerHeight - h - MARGIN)),
    })
  }, [x, y, items])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onClose, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onClose, true)
      window.removeEventListener('resize', onClose)
    }
  }, [onClose])

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      // 포털이어도 React 이벤트는 호출부 트리로 올라간다 — 메뉴 안 우클릭이 바깥 메뉴를 다시 열지 않게 막는다.
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      className="fixed z-[100] max-h-[60vh] min-w-[168px] overflow-y-auto rounded border py-1 text-[11px]"
      style={{
        top: pos.top,
        left: pos.left,
        borderColor: 'var(--border-subtle)',
        background: 'var(--color-bg-panel)',
        color: 'var(--color-text-body)',
        boxShadow: 'var(--elev-2)',
      }}
    >
      {items.map((it, i) =>
        it.divider ? (
          <div key={`d${i}`} className="my-1 border-t" style={{ borderColor: 'var(--border-subtle)' }} />
        ) : (
          <button
            key={`${it.label}-${i}`}
            type="button"
            role="menuitem"
            disabled={it.disabled}
            onClick={() => {
              onClose()
              it.onSelect?.()
            }}
            className={`flex w-full items-center truncate px-3 py-1.5 text-left transition-colors hover:bg-bg-input disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent ${it.danger ? 'text-red-400' : ''}`}
            style={{ paddingLeft: 12 + (it.depth ?? 0) * 12 }}
          >
            {it.label}
          </button>
        ),
      )}
    </div>,
    document.body,
  )
}
